import React from "react";
import { Check } from "lucide-react";
import { Reveal, Overline } from "./Reveal";
import { CTAButton } from "./CTAButton";

const accentMap = {
  signal: "text-signal",
  gold: "text-gold",
  health: "text-health",
};

export function FeatureList({ overline, title, items, accent = "signal", columns = 2 }) {
  return (
    <section className="py-20 md:py-28 border-t border-white/5">
      <div className="container-x">
        <Reveal>
          {overline && <Overline color={accent}>{overline}</Overline>}
          {title && (
            <h2 className="mt-4 font-display font-bold text-3xl sm:text-4xl tracking-tight max-w-3xl">{title}</h2>
          )}
        </Reveal>
        <div className={`mt-12 grid sm:grid-cols-2 ${columns === 3 ? "lg:grid-cols-3" : ""} gap-4`}>
          {items.map((it, i) => (
            <Reveal key={i} delay={i * 0.04}>
              <div className="flex items-start gap-3 surface p-6 surface-hover h-full" data-testid={`feature-${i}`}>
                <Check size={18} className={`mt-0.5 shrink-0 ${accentMap[accent]}`} />
                <div>
                  <p className="font-medium text-white">{typeof it === "string" ? it : it.title}</p>
                  {it.desc && <p className="mt-1.5 text-sm text-white/55 leading-relaxed">{it.desc}</p>}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export function StepFlow({ overline, title, steps, accent = "signal" }) {
  return (
    <section className="py-20 md:py-28 border-t border-white/5">
      <div className="container-x">
        <Reveal>
          {overline && <Overline color={accent}>{overline}</Overline>}
          {title && (
            <h2 className="mt-4 font-display font-bold text-3xl sm:text-4xl tracking-tight max-w-3xl">{title}</h2>
          )}
        </Reveal>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {steps.map((s, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <div className="surface p-6 h-full relative" data-testid={`step-${i}`}>
                <span className={`font-mono text-sm ${accentMap[accent]}`}>0{i + 1}</span>
                <h3 className="mt-3 font-display font-semibold text-white">{s.title}</h3>
                {s.desc && <p className="mt-2 text-sm text-white/55 leading-relaxed">{s.desc}</p>}
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export function CTASection({ title, subtitle, ctaLabel, ctaTo, ctaHref, variant = "primary", testId }) {
  return (
    <section className="py-20 md:py-28 border-t border-white/5">
      <div className="container-x">
        <Reveal>
          <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-navy-800 p-10 md:p-16 grain" data-testid={testId || "cta-section"}>
            <div className="absolute -top-24 -right-16 w-72 h-72 bg-signal/10 rounded-full blur-3xl" />
            <div className="relative max-w-2xl">
              <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight">{title}</h2>
              {subtitle && <p className="mt-4 text-white/60 leading-relaxed">{subtitle}</p>}
              <div className="mt-8">
                <CTAButton to={ctaTo} href={ctaHref} variant={variant} size="lg" testId="cta-section-button">
                  {ctaLabel}
                </CTAButton>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
